import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { CalendarBlank, Users, Baby, Tent } from "@phosphor-icons/react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ACCOMMODATIONS } from "@/lib/site";
import { getBookingConfig, getBookingUrl } from "@/lib/bookingConfig";

export default function BookingWidget({ overlap = true }) {
  const [checkIn, setCheckIn] = useState();
  const [checkOut, setCheckOut] = useState();
  const [adults, setAdults] = useState("2");
  const [children, setChildren] = useState("0");
  const [type, setType] = useState(ACCOMMODATIONS[0].id);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  const onSearch = async () => {
    const acc = ACCOMMODATIONS.find((a) => a.id === type);
    const arrival = checkIn ? format(checkIn, "yyyy-MM-dd") : "";
    const departure = checkOut ? format(checkOut, "yyyy-MM-dd") : "";

    setSearching(true);
    try {
      const cfg = await getBookingConfig();
      if (cfg.booking_mode === "rms") {
        const url = await getBookingUrl({
          category: acc?.rms_category_id || "",
          arrival,
          departure,
          adults,
          children,
        });
        if (url) {
          window.location.href = url;
          return;
        }
      }
    } finally {
      setSearching(false);
    }

    // Default (RMS not live yet) — route into the enquiry funnel with the selection prefilled.
    const params = new URLSearchParams();
    if (type) params.set("stay", type);
    if (arrival) params.set("check_in", arrival);
    if (departure) params.set("check_out", departure);
    if (adults) params.set("adults", adults);
    if (children) params.set("children", children);
    navigate(`/contact?${params.toString()}`);
  };

  return (
    <div
      data-testid="booking-widget"
      className={`relative z-30 mx-auto w-full max-w-5xl bg-white rounded-2xl border border-sa-ink/10 shadow-[0_18px_48px_rgba(26,54,40,0.12)] p-5 md:p-7 ${
        overlap ? "-mt-24 md:-mt-28" : ""
      }`}
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-3 items-end">
        <div className="md:col-span-3">
          <label className="overline block mb-2">Stay type</label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-12 border-sa-ink/15 bg-white" data-testid="booking-type">
              <Tent size={18} weight="duotone" className="text-sa-ink2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACCOMMODATIONS.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-2">
          <label className="overline block mb-2">Check-in</label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="booking-checkin"
                className="h-12 w-full flex items-center gap-2 border border-sa-ink/15 rounded-md px-3 bg-white hover:border-sa-ink/30 text-left text-sm"
              >
                <CalendarBlank size={16} weight="duotone" className="text-sa-ink2 shrink-0" />
                <span className="truncate">{checkIn ? format(checkIn, "d MMM") : <span className="text-sa-ink2">Select</span>}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0">
              <Calendar mode="single" selected={checkIn} onSelect={setCheckIn} initialFocus />
            </PopoverContent>
          </Popover>
        </div>

        <div className="md:col-span-2">
          <label className="overline block mb-2">Check-out</label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="booking-checkout"
                className="h-12 w-full flex items-center gap-2 border border-sa-ink/15 rounded-md px-3 bg-white hover:border-sa-ink/30 text-left text-sm"
              >
                <CalendarBlank size={16} weight="duotone" className="text-sa-ink2 shrink-0" />
                <span className="truncate">{checkOut ? format(checkOut, "d MMM") : <span className="text-sa-ink2">Select</span>}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0">
              <Calendar mode="single" selected={checkOut} onSelect={setCheckOut} initialFocus />
            </PopoverContent>
          </Popover>
        </div>

        <div className="md:col-span-2">
          <label className="overline block mb-2">Adults</label>
          <Select value={adults} onValueChange={setAdults}>
            <SelectTrigger className="h-12 border-sa-ink/15 bg-white" data-testid="booking-adults">
              <Users size={16} weight="duotone" className="text-sa-ink2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1,2,3,4,5,6,7,8].map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-2">
          <label className="overline block mb-2">Children</label>
          <Select value={children} onValueChange={setChildren}>
            <SelectTrigger className="h-12 border-sa-ink/15 bg-white" data-testid="booking-children">
              <Baby size={16} weight="duotone" className="text-sa-ink2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[0,1,2,3,4,5,6].map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-1">
          <button
            data-testid="booking-search-btn"
            onClick={onSearch}
            disabled={searching}
            className="h-12 w-full bg-sa-accent hover:bg-sa-accentHover disabled:opacity-60 text-white rounded-md flex items-center justify-center gap-2 transition-colors font-medium tracking-wide"
            aria-label="Book"
          >
            {searching ? "…" : "Book"}
          </button>
        </div>
      </div>
    </div>
  );
}
